import React, { memo } from 'react'
import { useMemoramaEngine } from '../../hooks/useMemoramaEngine'
import { useMemorama } from '../../context/MemoramaContext'
import { useRovingTabIndex } from '@hooks/useRovingTabIndex'
import { COLUMNS_BY_SIZE, PAIRS_BY_SIZE } from '../../constants/memorama'
import { useLanguage } from '@context/LanguageContext'
import { cx } from '@utils/cx'
import styles from './Board.module.css'

// Column class mapping
const columnStyles = {
  5: styles.boardCols5,
  6: styles.boardCols6,
  7: styles.boardCols7,
  8: styles.boardCols8,
  9: styles.boardCols9,
  10: styles.boardCols10,
  11: styles.boardCols11
}

function Board () {
  const { boardSize } = useMemorama()
  const { cards, handleCardClick, ariaMessage } = useMemoramaEngine()
  const { t } = useLanguage()

  const totalPairs = PAIRS_BY_SIZE[boardSize] || PAIRS_BY_SIZE.xs
  const totalCards = totalPairs * 2

  const columns = COLUMNS_BY_SIZE[boardSize] || COLUMNS_BY_SIZE.xs

  // 🐛 DEBUG: Temporary logging
  if (import.meta.env.MODE === 'development') {
    console.log('DEBUG Board.jsx:', { boardSize, columns, totalCards })
  }

  // Roving tabindex for keyboard navigation
  const { getTabIndex, getItemRef, handleKeyDown } = useRovingTabIndex({
    itemCount: totalCards,
    navigationStrategy: 'grid',
    gridColumns: columns,
    onActivate: (index) => {
      const card = cards[index]
      if (card && !card.flipped && !card.matched) {
        handleCardClick(card.id)
      }
    }
  })

  return (
    <>
      {/* ARIA-LIVE: menssage for SR with revealed card */}
      {ariaMessage && (
        <div className='sr-only' aria-live='polite'>
          {ariaMessage}
        </div>
      )}

      <div
        className={cx(styles.board, columnStyles[columns])}
        role='group'
        aria-label={t.memorama.access.board.grid}
        onKeyDown={handleKeyDown}
      >
        {cards.map((card, index) => {
          const cardName = card.translationKey
            ? t.memorama.cards[card.translationKey] || card.name
            : card.name

          const isRevealed = card.flipped || card.matched

          const cardLabel = isRevealed
            ? t.memorama.access.board.card.revealed.replace('{name}', cardName)
            : t.memorama.access.board.card.hidden

          return (
            <button
              key={card.id}
              ref={getItemRef(index)}
              tabIndex={getTabIndex(index)}
              type='button'
              role='button'
              aria-roledescription={t.memorama.access.board.card.role}
              aria-label={cardLabel}
              aria-posinset={index + 1}
              aria-setsize={totalCards}
              className={cx(
                styles.card,
                card.justMatched && styles.cardMatched,
                card.justMismatched && styles.cardMismatched,
                card.justMismatched && styles.cardMismatchedActive
              )}
              onClick={() => handleCardClick(card.id)}
            >
              <img
                src={
                  isRevealed
                    ? card.image || '/cards/fallback.webp'
                    : '/cards/card-back.webp'
              }
                alt={
                  isRevealed
                    ? t.memorama.access.board.card.revealed.replace('{name}', cardName)
                    : t.memorama.access.board.card.hidden
              }
                className={styles.cardImg}
                aria-hidden='true'
              />
            </button>
          )
        })}
      </div>
    </>
  )
}

export default memo(Board)
